// joesgrove API client. Thin typed wrapper around `fetch` that:
//
//   - sends `credentials: 'include'` so the encrypted session cookie rides
//     the request (set by /auth/callback on the server side)
//   - parses the standard `{error, code, details}` envelope into ApiError
//     so React-Query mutations branch on `err.code` instead of string match
//   - signals 401 via a dispatched window event so the router shell can
//     redirect to /auth/login without coupling fetch to the router
//
// All bodies are snake_case to match the Go DTOs verbatim. We deliberately
// don't translate between camelCase here — keeping wire shape and TS shape
// identical avoids one whole category of "I forgot to map field X" bugs.

import type { ErrorCode, ErrorEnvelope } from "./types";
import { ulid } from "ulid";

const API_BASE = ""; // same-origin; the binary serves both UI and API.

/** Event name dispatched on 401 so the router can react. */
export const AUTH_REQUIRED_EVENT = "joesgrove:auth-required";

/** Structured error surfaced to React-Query. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly details?: Record<string, unknown> | null;

  constructor(status: number, env: ErrorEnvelope) {
    super(env.error || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = env.code;
    this.details = env.details;
  }

  /** True when the error is the optimistic-lock STALE_WRITE 409. */
  get isStale(): boolean {
    return this.code === "STALE_WRITE";
  }

  /** True when the session cookie is missing/expired. */
  get isAuth(): boolean {
    return this.code === "UNAUTHENTICATED";
  }
}

/** Loose key/value pair for query-string params; null/undefined are dropped. */
export type QueryParams = {
  [key: string]: string | number | boolean | null | undefined;
};

interface RequestOptions {
  method?: string;
  body?: unknown;
  params?: QueryParams;
  /** Override the credentials mode for cross-origin testing. */
  credentials?: RequestCredentials;
  /** AbortSignal for React-Query cancellation. */
  signal?: AbortSignal;
}

/** Generate a client-side ULID (Crockford base-32). */
export function newULID(): string {
  return ulid();
}

/** Build a URL with safely-encoded query string. */
function buildURL(path: string, params?: RequestOptions["params"]): string {
  if (!params) return API_BASE + path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined) continue;
    qs.set(k, String(v));
  }
  const tail = qs.toString();
  return API_BASE + path + (tail ? "?" + tail : "");
}

/** Core request — parses the envelope on failure, returns the body on success. */
async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildURL(path, opts.params);
  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: opts.credentials ?? "include",
    headers: { Accept: "application/json" },
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    init.headers = {
      ...init.headers,
      "Content-Type": "application/json",
    };
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(url, init);

  if (res.status === 204) {
    // No content — for DELETE / void / dismiss / bulk-dismiss.
    return undefined as T;
  }

  const text = await res.text();
  const data = text ? safeJSON(text) : undefined;

  if (!res.ok) {
    const env: ErrorEnvelope =
      isErrorEnvelope(data)
        ? data
        : {
            error: text || res.statusText,
            code: "INTERNAL_ERROR",
            details: null,
          };
    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT));
    }
    throw new ApiError(res.status, env);
  }

  // Guard against successful responses that *aren't* JSON — typically the
  // embedded UI catch-all returning index.html for an API path that isn't
  // mounted (e.g. when the backend booted without a DB). Returning
  // `undefined` here would surface as "queryFn returned undefined" deep
  // inside TanStack Query, miles from the real cause.
  const ct = res.headers.get("Content-Type") ?? "";
  if (data === undefined || !ct.toLowerCase().includes("json")) {
    throw new ApiError(res.status, {
      error: `expected JSON from ${path}, got ${ct || "no content-type"}`,
      code: "INTERNAL_ERROR",
      details: { path, contentType: ct },
    });
  }
  return data as T;
}

function safeJSON(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function isErrorEnvelope(v: unknown): v is ErrorEnvelope {
  return (
    typeof v === "object" &&
    v !== null &&
    "code" in v &&
    typeof (v as Record<string, unknown>).code === "string"
  );
}

/** Public API used by the per-entity hook files. */
export const api = {
  get: <T>(path: string, params?: QueryParams, signal?: AbortSignal) =>
    request<T>(path, { method: "GET", params, signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: "POST", body, signal }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: "PATCH", body, signal }),
  delete: <T = void>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: "DELETE", body, signal }),
};
